import { Building2, KeyRound, User } from 'lucide-react';
import { useAccountStore } from '../stores/accountStore';
import { useAuthStore } from '../stores/authStore';
import { VenueCredStatus } from './VenueCredStatus';

/** #1114 T8: the quick reference on "Key mode unavailable" (the failed read only). */
const KEY_MODE_UNAVAILABLE_TITLE = "G1 couldn't read whether this login trades on org or personal keys.";

/**
 * Thin status strip shown above trader views: which credential pool is
 * currently active for THIS user. Reads the key-mode answer the account
 * store already fetched (/api/trader/key-mode, traders and managers alike),
 * so it adds no request and follows identity changes with the store.
 *
 * The strip keeps its height while that answer is pending or failed rather
 * than appearing late and pushing every trader page down (#1041). Only an
 * org-less user (e.g. an org-less admin) — known at sign-in — has no strip.
 */
export function KeyModeBanner() {
  const hasOrg = useAuthStore((s) => s.user?.orgId != null);
  const credentialsStatus = useAccountStore((s) => s.credentialsStatus);
  const info = useAccountStore((s) => s.keyModeInfo);
  const mode = info?.mode ?? null;

  if (!hasOrg) return null;

  const known = credentialsStatus === 'checked' && mode != null;
  const isOrg = known && mode === 'org';

  return (
    <div
      data-testid="key-mode-banner"
      className={`flex items-center justify-between gap-2 border-b px-4 py-1.5 text-[11px] ${
        !known
          ? 'border-border bg-muted/20 text-muted-foreground'
          : isOrg
            ? 'border-blue-500/20 bg-blue-500/5 text-blue-700 dark:text-blue-300'
            : 'border-amber-500/20 bg-amber-500/5 text-amber-700 dark:text-amber-300'
      }`}
    >
      <span className="flex items-center gap-2">
        {!known ? <KeyRound size={12} /> : isOrg ? <Building2 size={12} /> : <User size={12} />}
        {!known ? (
          <span role="status" title={credentialsStatus === 'error' ? KEY_MODE_UNAVAILABLE_TITLE : undefined}>
            {credentialsStatus === 'error' ? 'Key mode unavailable' : 'Checking keys…'}
          </span>
        ) : (
          <span>
            Trading via <strong>{isOrg ? 'ORG keys' : 'personal keys'}</strong>
            {info?.useOrgKeys && !info.orgKeysAvailable && (
              <span className="ml-1 opacity-80">
                (org has no keys; falling back to personal)
              </span>
            )}
          </span>
        )}
      </span>
      {/* Per-venue credential/connection dots (K/P) — the strip is always
          on for trader views, so venue health is always one glance away. */}
      <VenueCredStatus />
    </div>
  );
}
